/**
 * /admin/jobs — pg-boss job-state visibility + manual retry (OCP-68).
 *
 * Lists every queued/active/completed/failed job, filterable by
 * queue name and state. Clicking a row navigates to the detail view
 * where the operator can inspect the input payload + error and
 * trigger a retry for failed/cancelled jobs.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

type JobState = 'created' | 'retry' | 'active' | 'completed' | 'cancelled' | 'failed';

interface JobItem {
  id: string;
  name: string;
  state: JobState;
  retry_count: number;
  retry_limit: number;
  created_on: string;
  started_on: string | null;
  completed_on: string | null;
  runtime_seconds: number | null;
}

interface JobDetail extends JobItem {
  priority: number;
  data: unknown;
  output: unknown;
  retry_delay: number;
  retry_backoff: boolean;
  start_after: string;
  keep_until: string;
  expire_in: string;
  dead_letter: string | null;
  policy: string | null;
  singleton_key: string | null;
}

interface QueueSummary {
  name: string;
  counts: Partial<Record<JobState, number>>;
  total: number;
}

const ALL_STATES: JobState[] = ['failed', 'retry', 'active', 'created', 'completed', 'cancelled'];

export function AdminJobsPage() {
  const [queueFilter, setQueueFilter] = useState<string>('');
  const [stateFilter, setStateFilter] = useState<Set<JobState>>(new Set(['failed', 'retry']));
  const [queues, setQueues] = useState<QueueSummary[]>([]);
  const [items, setItems] = useState<JobItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (queueFilter) params.set('name', queueFilter);
    if (stateFilter.size > 0 && stateFilter.size < ALL_STATES.length) {
      params.set('state', Array.from(stateFilter).join(','));
    }
    params.set('limit', '100');
    return params.toString() ? `?${params.toString()}` : '';
  }, [queueFilter, stateFilter]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [list, summary] = await Promise.all([
        api<{ total: number; items: JobItem[] }>(`/v1/admin/jobs${queryString}`),
        api<{ items: QueueSummary[] }>('/v1/admin/jobs/queues'),
      ]);
      setItems(list.items);
      setTotal(list.total);
      setQueues(summary.items);
    } catch (err) {
      if (err instanceof ApiError) setError(`Could not load jobs (HTTP ${err.status}).`);
      else setError('Could not load jobs.');
    }
  }, [queryString]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleState = useCallback((s: JobState) => {
    setStateFilter((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }, []);

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      <aside className="w-full shrink-0 lg:w-56">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Filter</h2>
        <div className="space-y-4">
          <div>
            <span className="text-sm text-gray-700 dark:text-gray-300">Queue</span>
            <select
              value={queueFilter}
              onChange={(e) => setQueueFilter(e.target.value)}
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm"
            >
              <option value="">Any</option>
              {queues.map((q) => (
                <option key={q.name} value={q.name}>
                  {q.name} ({q.total})
                </option>
              ))}
            </select>
          </div>
          <div>
            <span className="text-sm text-gray-700 dark:text-gray-300">State</span>
            <div className="mt-1 space-y-1">
              {ALL_STATES.map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={stateFilter.has(s)}
                    onChange={() => toggleState(s)}
                  />
                  <StateBadge state={s} />
                </label>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setQueueFilter('');
              setStateFilter(new Set(ALL_STATES));
            }}
            className="text-xs text-gray-500 underline-offset-2 hover:underline"
          >
            Show everything
          </button>
        </div>

        {queues.length > 0 && (
          <div className="mt-6">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Queue summary
            </h3>
            <ul className="space-y-2 text-xs">
              {queues.map((q) => (
                <li key={q.name} className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-2">
                  <div className="truncate font-medium text-gray-800 dark:text-gray-200">{q.name}</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {(Object.keys(q.counts) as JobState[]).map((s) => (
                      <span key={s}>
                        <StateBadge state={s} />
                        <span className="ml-1 tabular-nums text-gray-700 dark:text-gray-300">{q.counts[s]}</span>
                      </span>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </aside>

      <section className="flex-1">
        <div className="mb-4 flex items-baseline justify-between">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">pg-boss jobs</h1>
          <div className="text-sm text-gray-500">
            {items && `${items.length} of ${total}`}
          </div>
        </div>

        {error && (
          <div role="alert" className="mb-3 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
            {error}
          </div>
        )}

        {!items && !error && <p className="text-sm text-gray-500">Loading…</p>}

        {items && items.length === 0 && (
          <div className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-4 py-6 text-sm text-gray-600 dark:text-gray-400">
            No jobs match the current filter.
          </div>
        )}

        {items && items.length > 0 && (
          <div className="overflow-x-auto rounded border border-gray-200 dark:border-gray-800">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-600 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-2">Queue</th>
                  <th className="px-3 py-2">State</th>
                  <th className="px-3 py-2">Retries</th>
                  <th className="px-3 py-2">Created</th>
                  <th className="px-3 py-2">Runtime</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800 bg-white dark:bg-gray-900">
                {items.map((j) => (
                  <tr key={j.id}>
                    <td className="px-3 py-2 font-mono text-xs text-gray-700 dark:text-gray-300">{j.name}</td>
                    <td className="px-3 py-2">
                      <StateBadge state={j.state} />
                    </td>
                    <td className="px-3 py-2 tabular-nums text-gray-700 dark:text-gray-300">
                      {j.retry_count}/{j.retry_limit}
                    </td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                      {fmtDateTime(j.created_on)}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-gray-600 dark:text-gray-400">
                      {j.runtime_seconds !== null ? `${j.runtime_seconds}s` : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Link
                        to={`/jobs/${j.id}`}
                        className="text-xs text-brand-500 hover:text-brand-600"
                      >
                        Inspect →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

export function AdminJobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<JobDetail | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    setNotFound(false);
    try {
      const r = await api<{ job: JobDetail }>(`/v1/admin/jobs/${id}`);
      setJob(r.job);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setNotFound(true);
      else if (err instanceof ApiError) setError(`Could not load job (HTTP ${err.status}).`);
      else setError('Could not load job.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRetry = useCallback(async () => {
    if (!job) return;
    if (!window.confirm(`Retry job ${job.id.slice(0, 8)}…? It will re-run with the same input.`)) {
      return;
    }
    setRetrying(true);
    setError(null);
    try {
      const r = await api<{ job: JobDetail }>(`/v1/admin/jobs/${job.id}/retry`, {
        method: 'POST',
      });
      setJob(r.job);
    } catch (err) {
      if (err instanceof ApiError) {
        const reason = (err.body as { error?: string; message?: string } | null) ?? {};
        if (reason.error === 'job_not_retryable') {
          setError(reason.message ?? 'Job cannot be retried in its current state.');
        } else {
          setError(`Could not retry (HTTP ${err.status}).`);
        }
      } else {
        setError('Could not retry.');
      }
    } finally {
      setRetrying(false);
    }
  }, [job]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-3xl">
        <button
          type="button"
          onClick={() => navigate('/jobs')}
          className="text-sm text-brand-500 hover:text-brand-600"
        >
          ← Back to jobs
        </button>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight">Job not found</h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          No pg-boss job matches this id. pg-boss prunes job rows after `keep_until` (default 14 days);
          older runs may have been garbage-collected.
        </p>
      </div>
    );
  }

  const retryable = job && (job.state === 'failed' || job.state === 'cancelled');

  return (
    <div className="mx-auto max-w-4xl">
      <button
        type="button"
        onClick={() => navigate('/jobs')}
        className="text-sm text-brand-500 hover:text-brand-600"
      >
        ← Back to jobs
      </button>

      {error && (
        <div role="alert" className="mt-3 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {!job && !error ? (
        <p className="mt-6 text-sm text-gray-500">Loading…</p>
      ) : job ? (
        <>
          <div className="mt-2 flex items-baseline justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">{job.name}</h1>
              <p className="mt-1 font-mono text-xs text-gray-500">{job.id}</p>
            </div>
            <StateBadge state={job.state} />
          </div>

          <section className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Retry" value={`${job.retry_count} of ${job.retry_limit}`} />
            <Field label="Retry delay" value={`${job.retry_delay}s`} />
            <Field
              label="Backoff"
              value={job.retry_backoff ? 'exponential' : 'linear'}
            />
            <Field
              label="Created"
              value={fmtDateTime(job.created_on)}
            />
            <Field
              label="Started"
              value={job.started_on ? fmtDateTime(job.started_on) : '—'}
            />
            <Field
              label="Completed"
              value={job.completed_on ? fmtDateTime(job.completed_on) : '—'}
            />
            <Field
              label="Runtime"
              value={job.runtime_seconds !== null ? `${job.runtime_seconds}s` : '—'}
            />
            <Field label="Priority" value={String(job.priority)} />
            <Field label="Expires in" value={job.expire_in} />
          </section>

          <section className="mt-8">
            <h2 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-200">Input data</h2>
            <pre className="overflow-x-auto rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 p-3 text-xs">
              {JSON.stringify(job.data, null, 2)}
            </pre>
          </section>

          <section className="mt-6">
            <h2 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-200">Output / error</h2>
            <pre className="overflow-x-auto rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 p-3 text-xs">
              {job.output === null ? '— (no output recorded)' : JSON.stringify(job.output, null, 2)}
            </pre>
          </section>

          {retryable && (
            <section className="mt-8 rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/30 p-4">
              <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-200">Retry this job</h2>
              <p className="mt-1 text-sm text-amber-800 dark:text-amber-200">
                Flips the job back to <code>retry</code> with <code>start_after=now()</code>. The
                pg-boss worker picks it up on the next poll and re-runs with the same input above.
                Only available because the current state (<strong>{job.state}</strong>) is
                irrecoverable without your action.
              </p>
              <button
                type="button"
                onClick={handleRetry}
                disabled={retrying}
                className="mt-3 rounded bg-amber-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:bg-amber-300"
              >
                {retrying ? 'Queuing…' : 'Retry job'}
              </button>
            </section>
          )}
        </>
      ) : null}
    </div>
  );
}

// ---- shared sub-components -----------------------------------------

function StateBadge({ state }: { state: JobState }) {
  const styles: Record<JobState, string> = {
    completed: 'bg-green-100 text-green-800',
    active: 'bg-blue-100 text-blue-800 dark:text-blue-300',
    retry: 'bg-amber-100 text-amber-800 dark:text-amber-200',
    failed: 'bg-red-100 text-red-800 dark:text-red-300',
    cancelled: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
    created: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
  };
  return (
    <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${styles[state]}`}>
      {state}
    </span>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">{value}</div>
    </div>
  );
}
